import { createCRUDHook } from './createCRUDHook';
import {
  FilterOptionModel,
  JsonPatch,
  KiItemsDto,
  KitCompleteDetailsDto,
  KitFilterOptionsModel,
  KitsDto,
} from 'utils/models';
import { AxiosResponse } from 'axios';
import {
  CREATE_BULK_ENTITY,
  FILTERED_ENTITIES,
  GET_FILTERED_PARTS_KITS,
  GET_KIT_COMPLETE_DETAILS,
  TBL_PART_KIT,
  TBL_PART_KIT_ITEM,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';

interface KitsCustomOperations {
  getKitsDataBySP: (
    filteredEntities: KitFilterOptionsModel,
  ) => Promise<KitsDto[]>;
  getKitCompleteDetails: (id: number) => Promise<KitCompleteDetailsDto>;
  addKitItemData: (param: KiItemsDto) => Promise<KiItemsDto>;
  addBulkKitItemData: (param: KiItemsDto[]) => Promise<boolean>;
  getKitItemsDataByFE: (
    filteredEntities: FilterOptionModel,
  ) => Promise<KiItemsDto[]>;
  updateKitItemsData: (
    param: JsonPatch[],
    id: number,
  ) => Promise<AxiosResponse>;
  deleteKitItemData: (id: number) => Promise<boolean>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  KitsDto,
  KitsDto[],
  KitsCustomOperations
>({
  entityName: 'Kits',
  endpoint: TBL_PART_KIT,

  customOperations: (auth) => ({
    getKitsDataBySP: async (
      filteredEntities: KitFilterOptionsModel,
    ): Promise<KitsDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PART_KIT +
            GET_FILTERED_PARTS_KITS +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getKitCompleteDetails: async (
      id: number,
    ): Promise<KitCompleteDetailsDto> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PART_KIT + GET_KIT_COMPLETE_DETAILS + `?kitId=${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addKitItemData: async (param: KiItemsDto): Promise<KiItemsDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(TBL_PART_KIT_ITEM, param);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addBulkKitItemData: async (param: KiItemsDto[]): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          TBL_PART_KIT_ITEM + CREATE_BULK_ENTITY,
          param,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getKitItemsDataByFE: async (
      filteredEntities: FilterOptionModel,
    ): Promise<KiItemsDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PART_KIT_ITEM +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateKitItemsData: async (
      param: JsonPatch[],
      id: number,
    ): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${TBL_PART_KIT_ITEM}/${id}`,
          param,
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deleteKitItemData: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${TBL_PART_KIT_ITEM}/${id}`,
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },
  }),
});

export const KitsContext = Context;
export const useKitsManager = useManager;
export const useKits = useConsumer;
